import type { AxiosResponse } from "axios"
import type { OpenAPIV3 } from "openapi-types"

import type { UpdateDataFileRequest, UploadDataFileRequest } from "../features/DataFiles/interfaces"
import type { CreateScoreCardRequest, UpdateScorecardRequest } from "../features/Scorecards/Interfaces"
import type {
  CancelBuildingScriptRequest,
  CreateBuiltinScriptRequest,
  CreateScriptRequest,
  DeleteScriptCredsRequest,
  StatusTextMap,
  TestScriptRequest,
  UpdateScriptRequest,
} from "../features/Scripts/Interfaces"
import { apiClient } from "../lib/axios"
import type { CreateIssueRequest } from "../types/custom/core"
import type { CreateExportJobRequestProps } from "../types/custom/dataFiles"
import type {
  ConfigureAutoMlModelRequest,
  CreateAutoMlRequest,
  CreateModelRequest,
  CreateModelResponse,
  CreateProgramRequest,
  CreateProjectRequest,
  CreateRetrainingModelRequest,
  CreateScorecardSetRequest,
  CreateWorkflowRequest,
  DeleteModelRequest,
  DriftResponse,
  ModelTimeRange,
  ProjectLimits,
  ProjectTimeRange,
  RequiredSchema,
  ScriptLogsRange,
  StatisticsResponse,
  TaskId,
  TrainModelUsingPrebuiltModelRequest,
  UpdateProgramRequest,
  UpdateScorecardsetRequest,
  UpdateWorkflowRequest,
  WorkflowSimulationJobCreateRequest,
  WorkflowSimulationJobFetchRequest,
  WorkflowSimulationOutputFetchRequest,
} from "../types/custom/projects"
import type {
  CloneDataFilesRequest,
  CreateRulesetRequest,
  CreateTaglistRequest,
  RetrieveLabelsRequest,
  UpdateRulesetRequest,
  UpdateTaglistRequest,
  VersionChangeRequest,
} from "../types/custom/rules"
import type { ActualDecisionCreateRequest } from "../types/generated/api/ActualDecisionCreateRequest"
import type { ActualDecisionCreateResponse } from "../types/generated/api/ActualDecisionCreateResponse"
import type { ActualDecisionCreationJob } from "../types/generated/api/ActualDecisionCreationJob"
import type { ActualDecisionFillResponse } from "../types/generated/api/ActualDecisionFillResponse"
import type { AutoMLJobLogs } from "../types/generated/api/AutoMLJobLogs"
import type { BulkCreateFeedbackRequest } from "../types/generated/api/BulkCreateFeedbackRequest"
import type { BulkCreateFeedbackResponse } from "../types/generated/api/BulkCreateFeedbackResponse"
import type { CalculatorCreateRequest } from "../types/generated/api/CalculatorCreateRequest"
import type { CalculatorGroupRetrieve } from "../types/generated/api/CalculatorGroupRetrieve"
import type { CalculatorVersionRetrieve } from "../types/generated/api/CalculatorVersionRetrieve"
import type { ClassificationEvaluation } from "../types/generated/api/ClassificationEvaluation"
import type { ComponentTest } from "../types/generated/api/ComponentTest"
import type { DataFile } from "../types/generated/api/DataFile"
import type { DataFileCloneResponse } from "../types/generated/api/DataFileCloneResponse"
import type { DataFileGroup } from "../types/generated/api/DataFileGroup"
import type { Deployment } from "../types/generated/api/Deployment"
import type { DeploymentLatestFeedbackDateTime } from "../types/generated/api/DeploymentLatestFeedbackDateTime"
import type { DeploymentLogs } from "../types/generated/api/DeploymentLogs"
import type { DeploymentModelsAvgResponseTime } from "../types/generated/api/DeploymentModelsAvgResponseTime"
import type { DeploymentPredictionsSummary } from "../types/generated/api/DeploymentPredictionsSummary"
import type { DeploymentSchemaFeatures } from "../types/generated/api/DeploymentSchemaFeatures"
import type { DeploymentUsage } from "../types/generated/api/DeploymentUsage"
import type { EvaluateResult } from "../types/generated/api/EvaluateResult"
import type { ExportJobCreateRequest } from "../types/generated/api/ExportJobCreateRequest"
import type { ExportJobRetrieve } from "../types/generated/api/ExportJobRetrieve"
import type { FeaturesCountsInsight } from "../types/generated/api/FeaturesCountsInsight"
import type { FuzzyMatchRequest } from "../types/generated/api/FuzzyMatchRequest"
import type { KonanBasePredictionResponse } from "../types/generated/api/KonanBasePredictionResponse"
import type { KonanRegistryImagesSeriliazer } from "../types/generated/api/KonanRegistryImagesSeriliazer"
import type { KonanRegistryToken } from "../types/generated/api/KonanRegistryToken"
import type { Label } from "../types/generated/api/Label"
import type { ListClassificationModelOutputs } from "../types/generated/api/ListClassificationModelOutputs"
import type { Model } from "../types/generated/api/Model"
import type { ModelSettingsSet } from "../types/generated/api/ModelSettingsSet"
import type { ModelSettingsSetRequest } from "../types/generated/api/ModelSettingsSetRequest"
import type { PaginatedActualDecisionCreationJobList } from "../types/generated/api/PaginatedActualDecisionCreationJobList"
import type { PaginatedActualDecisionList } from "../types/generated/api/PaginatedActualDecisionList"
import type { PaginatedAutoMLTrainingJobList } from "../types/generated/api/PaginatedAutoMLTrainingJobList"
import type { PaginatedCalculatorListList } from "../types/generated/api/PaginatedCalculatorListList"
import type { PaginatedCalculatorVersionRetrieveList } from "../types/generated/api/PaginatedCalculatorVersionRetrieveList"
import type { PaginatedDataFileGroupListList } from "../types/generated/api/PaginatedDataFileGroupListList"
import type { PaginatedDataFileVersionsListList } from "../types/generated/api/PaginatedDataFileVersionsListList"
import type { PaginatedDeploymentListList } from "../types/generated/api/PaginatedDeploymentListList"
import type { PaginatedDownloadExportJobList } from "../types/generated/api/PaginatedDownloadExportJobList"
import type { PaginatedExportJobListList } from "../types/generated/api/PaginatedExportJobListList"
import type { PaginatedFuzzyMatchList } from "../types/generated/api/PaginatedFuzzyMatchList"
import type { PaginatedLabelList } from "../types/generated/api/PaginatedLabelList"
import type { PaginatedListClassificationModelOutputsList } from "../types/generated/api/PaginatedListClassificationModelOutputsList"
import type { PaginatedListModelOutputsList } from "../types/generated/api/PaginatedListModelOutputsList"
import type { PaginatedListModelStateSwitchList } from "../types/generated/api/PaginatedListModelStateSwitchList"
import type { PaginatedListPredictionsList } from "../types/generated/api/PaginatedListPredictionsList"
import type { PaginatedListRequestLogList } from "../types/generated/api/PaginatedListRequestLogList"
import type { PaginatedModelListCreateList } from "../types/generated/api/PaginatedModelListCreateList"
import type { PaginatedModelSettingsSetList } from "../types/generated/api/PaginatedModelSettingsSetList"
import type { PaginatedPredefinedTypeListList } from "../types/generated/api/PaginatedPredefinedTypeListList"
import type { PaginatedPredictionReviewList } from "../types/generated/api/PaginatedPredictionReviewList"
import type { PaginatedProgramList } from "../types/generated/api/PaginatedProgramList"
import type { PaginatedProgramVersionListList } from "../types/generated/api/PaginatedProgramVersionListList"
import type { PaginatedReportListCreateList } from "../types/generated/api/PaginatedReportListCreateList"
import type { PaginatedRetrainingJobList } from "../types/generated/api/PaginatedRetrainingJobList"
import type { PaginatedRuleSetListList } from "../types/generated/api/PaginatedRuleSetListList"
import type { PaginatedRuleSetVersionsListList } from "../types/generated/api/PaginatedRuleSetVersionsListList"
import type { PaginatedScoreCardListList } from "../types/generated/api/PaginatedScoreCardListList"
import type { PaginatedScoreCardSetListList } from "../types/generated/api/PaginatedScoreCardSetListList"
import type { PaginatedScoreCardSetVersionsListList } from "../types/generated/api/PaginatedScoreCardSetVersionsListList"
import type { PaginatedScoreCardVersionsListList } from "../types/generated/api/PaginatedScoreCardVersionsListList"
import type { PaginatedScriptGroupListList } from "../types/generated/api/PaginatedScriptGroupListList"
import type { PaginatedScriptTemplateList } from "../types/generated/api/PaginatedScriptTemplateList"
import type { PaginatedScriptVersionList } from "../types/generated/api/PaginatedScriptVersionList"
import type { PaginatedSimulationJobRetrieveList } from "../types/generated/api/PaginatedSimulationJobRetrieveList"
import type { PaginatedTagListListList } from "../types/generated/api/PaginatedTagListListList"
import type { PaginatedTagListVersionListList } from "../types/generated/api/PaginatedTagListVersionListList"
import type { PaginatedTrainingJobList } from "../types/generated/api/PaginatedTrainingJobList"
import type { PaginatedWorkflowListList } from "../types/generated/api/PaginatedWorkflowListList"
import type { PaginatedWorkflowSimulationOutputList } from "../types/generated/api/PaginatedWorkflowSimulationOutputList"
import type { PaginatedWorkflowVersionListList } from "../types/generated/api/PaginatedWorkflowVersionListList"
import type { PatchedCalculatorUpdateRequest } from "../types/generated/api/PatchedCalculatorUpdateRequest"
import type { PatchedConfigureAutoMLRequest } from "../types/generated/api/PatchedConfigureAutoMLRequest"
import type { PatchedScoreCardSetCreateUpdateRequest } from "../types/generated/api/PatchedScoreCardSetCreateUpdateRequest"
import type { PatchedUpdateFeedbackRequest } from "../types/generated/api/PatchedUpdateFeedbackRequest"
import type { PredictionReview } from "../types/generated/api/PredictionReview"
import type { PredictionReviewRequest } from "../types/generated/api/PredictionReviewRequest"
import type { Program } from "../types/generated/api/Program"
import type { ProgramRetrieve } from "../types/generated/api/ProgramRetrieve"
import type { ProgramVersionRetrieve } from "../types/generated/api/ProgramVersionRetrieve"
import type { Report } from "../types/generated/api/Report"
import type { ReportListCreateRequest } from "../types/generated/api/ReportListCreateRequest"
import type { RequestSummaryDailyOutput } from "../types/generated/api/RequestSummaryDailyOutput"
import type { RequestSummaryOutput } from "../types/generated/api/RequestSummaryOutput"
import type { RetrainingJobLogs } from "../types/generated/api/RetrainingJobLogs"
import type { RuleSetGroupRetrieve } from "../types/generated/api/RuleSetGroupRetrieve"
import type { RuleSetVersionRetrieve } from "../types/generated/api/RuleSetVersionRetrieve"
import type { ScoreCardCreateUpdate } from "../types/generated/api/ScoreCardCreateUpdate"
import type { ScoreCardGroupRetrieve } from "../types/generated/api/ScoreCardGroupRetrieve"
import type { ScoreCardRetrieve } from "../types/generated/api/ScoreCardRetrieve"
import type { ScoreCardSetGroupRetrieve } from "../types/generated/api/ScoreCardSetGroupRetrieve"
import type { ScoreCardSetRetrieve } from "../types/generated/api/ScoreCardSetRetrieve"
import type { ScriptGroupRetrieve } from "../types/generated/api/ScriptGroupRetrieve"
import type { ScriptTemplateRetrieve } from "../types/generated/api/ScriptTemplateRetrieve"
import type { ScriptTestOutput } from "../types/generated/api/ScriptTestOutput"
import type { ScriptVersionRetrieve } from "../types/generated/api/ScriptVersionRetrieve"
import type { TableColumnConfigurationRequest } from "../types/generated/api/TableColumnConfigurationRequest"
import type { TableConfigurationRequest } from "../types/generated/api/TableConfigurationRequest"
import type { TagListGroupRetrieve } from "../types/generated/api/TagListGroupRetrieve"
import type { TagListVersionRetrieve } from "../types/generated/api/TagListVersionRetrieve"
import type { TrainingDataResponse } from "../types/generated/api/TrainingDataResponse"
import type { TrainingDataSchemaResponse } from "../types/generated/api/TrainingDataSchemaResponse"
import type { TrainingDataUpload } from "../types/generated/api/TrainingDataUpload"
import type { UpdateFeedback } from "../types/generated/api/UpdateFeedback"
import type { Version } from "../types/generated/api/Version"
import type { WorkflowGroupRetrieve } from "../types/generated/api/WorkflowGroupRetrieve"
import type { WorkflowOutputResponse } from "../types/generated/api/WorkflowOutputResponse"
import type { WorkflowResponseFilter } from "../types/generated/api/WorkflowResponseFilter"
import type { WorkflowSimulationJobCreate } from "../types/generated/api/WorkflowSimulationJobCreate"
import type { WorkflowVersionRetrieve } from "../types/generated/api/WorkflowVersionRetrieve"
import { getHostnames } from "../utils/genericHelpers"
import type { DeploymentPredictionNextRunAt } from "./../types/generated/api/DeploymentPredictionNextRunAt"

const { backend } = getHostnames()

export const KonanAPI = {
  fetchKonanPermissions: async (): Promise<AxiosResponse> => {
    const { data } = await apiClient.fetchResource(backend, `/permissions/`)
    return data
  },

  fetchRegistryImages: async (): Promise<Array<KonanRegistryImagesSeriliazer>> => {
    const { data } = await apiClient.fetchResource(backend, `/registry/images/`)
    return data
  },

  fetchRegistryToken: async (): Promise<KonanRegistryToken> => {
    const { data } = await apiClient.fetchResource(backend, `/registry/token/`)
    return data
  },

  fetchProjects: async (props: {
    page?: number
    pageSize?: number
    search?: string
    category?: "model" | "workflow"
    type?: ("generic" | "classification" | "credit_scoring")[]
    ordering?: "-created_at" | "name"
  }): Promise<AxiosResponse<PaginatedDeploymentListList>> => {
    const { page, pageSize, category, type, search, ordering } = props

    return apiClient.fetchResource(backend, `/projects/`, {
      page,
      page_size: pageSize,
      category,
      type,
      search,
      ordering: ordering,
    })
  },

  fetchProject: async (uuid: string): Promise<AxiosResponse<Deployment>> => {
    return apiClient.fetchResource(backend, `/projects/${uuid}/`)
  },

  fetchProjectDocs: async (uuid: string): Promise<AxiosResponse<OpenAPIV3.Document>> => {
    return apiClient.fetchResource(backend, `/ml-docs/${uuid}/`)
  },

  fetchModelDocs: async (uuid: string): Promise<AxiosResponse<OpenAPIV3.Document>> => {
    return apiClient.fetchResource(backend, `/models/${uuid}/docs/`)
  },

  fetchBackendVersion: async (): Promise<AxiosResponse<Version>> => {
    return apiClient.fetchResource(backend, `/version/`)
  },

  createProject: async (props: CreateProjectRequest): Promise<AxiosResponse<Deployment>> => {
    return apiClient.postResource(backend, `/projects/`, {
      name: props.name,
      type: props.type,
      category: props.category,
      description: props.description ? props.description : undefined,
    })
  },

  deleteProject: async (projectId: string): Promise<AxiosResponse> => {
    return apiClient.deleteResource(backend, `/projects/${projectId}/`)
  },

  sendPrediction: async (
    projectId: string,
    predictionBody: Record<PropertyKey, unknown> | undefined,
  ): Promise<AxiosResponse<KonanBasePredictionResponse>> => {
    return apiClient.postResource(backend, `/projects/${projectId}/predict/`, {
      ...predictionBody,
    })
  },

  sendModelPredictionTest: async (
    modelId: string,
    predictionBody: Record<PropertyKey, unknown>,
    skip_config: boolean = false,
    abortSignal?: AbortSignal,
  ): Promise<AxiosResponse<KonanBasePredictionResponse>> => {
    return apiClient.postResource(
      backend,
      `/projects/${modelId}/predictions/test/`,
      {
        ...predictionBody,
      },
      {
        params: {
          skip_config,
        },
        signal: abortSignal,
      },
    )
  },

  sendWorkflowPredictionTest: async (
    workflowId: string,
    predictionBody: Record<PropertyKey, unknown>,
  ): Promise<AxiosResponse<WorkflowOutputResponse>> => {
    return apiClient.postResource(backend, `/projects/workflows/${workflowId}/test/`, {
      ...predictionBody,
    })
  },

  fetchRequestLogData: async (
    startDate: string,
    endDate: string,
    projectId: string,
    page: number,
    pageSize: number,
  ): Promise<AxiosResponse<PaginatedListRequestLogList>> => {
    return apiClient.fetchResource(backend, `/projects/${projectId}/requests/`, {
      start_time: startDate,
      end_time: endDate,
      page: page,
      page_size: pageSize,
    })
  },

  fetchRequestSummary: async ({
    start_date,
    end_date,
    project_uuid,
  }: ProjectTimeRange): Promise<AxiosResponse<RequestSummaryOutput>> => {
    return apiClient.fetchResource(backend, `/projects/${project_uuid}/requests/summary/`, {
      start_time: start_date,
      end_time: end_date,
    })
  },

  fetchDailyProjectRequestSummary: async ({
    start_date,
    end_date,
    project_uuid,
  }: ProjectTimeRange): Promise<RequestSummaryDailyOutput> => {
    const { data } = await apiClient.fetchResource(backend, `/projects/${project_uuid}/predictions/summary/daily/`, {
      start_time: start_date,
      end_time: end_date,
    })
    return data
  },

  fetchDailyModelRequestSummary: async ({
    start_date,
    end_date,
    model_uuid,
  }: ModelTimeRange): Promise<RequestSummaryDailyOutput> => {
    const { data } = await apiClient.fetchResource(
      backend,
      `/projects/models/${model_uuid}/predictions/summary/daily/`,
      {
        start_time: start_date,
        end_time: end_date,
      },
    )
    return data
  },

  fetchProjectAverageResponseTime: async ({
    start_date,
    end_date,
    project_uuid,
  }: ProjectTimeRange): Promise<AxiosResponse<DeploymentModelsAvgResponseTime>> => {
    return apiClient.fetchResource(backend, `/projects/${project_uuid}/metrics/response-time/avg/`, {
      start_time: start_date,
      end_time: end_date,
    })
  },

  fetchModelLogs: async ({ start_date, end_date, model_uuid }: ModelTimeRange): Promise<DeploymentLogs> => {
    const { data } = await apiClient.fetchResource(backend, `/projects/models/${model_uuid}/logs/`, {
      model_uuid: model_uuid,
      start_time: start_date,
      end_time: end_date,
    })
    return data
  },

  switchModelState: async ({
    projectId,
    modelUUID,
    newState,
  }: {
    projectId: string | undefined
    modelUUID: string
    newState: string
  }): Promise<AxiosResponse> => {
    return apiClient.postResource(backend, `/projects/${projectId}/switch/`, {
      new_state: newState,
      switching_model: modelUUID,
    })
  },

  createStatisticsRun: async ({ start_date, end_date, project_uuid }: ProjectTimeRange): Promise<TaskId> => {
    const { data } = await apiClient.postResource(backend, `/projects/${project_uuid}/statistics/`, {
      project_uuid: project_uuid,
      start_time: start_date,
      end_time: end_date,
    })
    return data
  },

  fetchStatistics: async (taskId: string): Promise<AxiosResponse<StatisticsResponse>> => {
    return apiClient.fetchResource(backend, `/projects/tasks/${taskId}/`)
  },

  uploadTrainingData: async (
    project_uuid: string,
    file: File,
    setProgress: (progress: number) => void,
    signal: AbortSignal,
  ): Promise<TrainingDataResponse> => {
    const formData = new FormData()
    formData.append("training_data_file", file)
    const { data } = await apiClient.postResource(
      backend,
      `/projects/${project_uuid}/training-data/upload/`,
      formData,
      {
        onUploadProgress: function (progressEvent) {
          // TODO:: check api later
          progressEvent.total && setProgress((progressEvent.loaded / progressEvent.total) * 100)
        },
        signal,
      },
    )
    return data
  },

  fetchTrainingData: async (project_uuid: string): Promise<TrainingDataUpload[]> => {
    const { data } = await apiClient.fetchResource(backend, `/projects/${project_uuid}/training-data/`)
    return data
  },

  deleteTrainingDataFromModel: async (model_uuid: string): Promise<AxiosResponse> => {
    return apiClient.deleteResource(backend, `/projects/models/${model_uuid}/training_data/`)
  },

  deleteTrainingDataFromProject: async (project_uuid: string, training_uuid: string): Promise<AxiosResponse> => {
    return apiClient.deleteResource(backend, `/projects/${project_uuid}/training-data/${training_uuid}/`)
  },

  AssignTrainingDataToModel: async (model_uuid: string, training_data: string): Promise<AxiosResponse> => {
    return apiClient.postResource(backend, `/projects/models/${model_uuid}/training_data/`, {
      training_data: training_data,
    })
  },

  fetchSingleTrainingData: async (
    project_uuid: string,
    training_data_uuid: string | undefined,
  ): Promise<AxiosResponse<TrainingDataUpload>> => {
    return apiClient.fetchResource(backend, `/projects/${project_uuid}/training-data/${training_data_uuid}/`)
  },

  fetchDataDriftJobs: async (
    modelUuid: string,
    page?: number,
    pageSize?: number,
  ): Promise<AxiosResponse<DriftResponse>> => {
    return apiClient.fetchResource(backend, `/projects/models/${modelUuid}/data-drift-jobs/`, {
      page: page,
      page_size: pageSize,
    })
  },

  fetchProjectPredictions: async (
    startDate: string,
    endDate: string,
    projectId: string,
    page: number,
    pageSize: number,
    filterObject?: object,
    review_label?: "REVIEWED" | "REVOKED",
    joint_output?: string,
    reason?: string,
    feedback?: string,
  ): Promise<AxiosResponse<PaginatedListPredictionsList>> => {
    return apiClient.fetchResource(backend, `/projects/${projectId}/predictions/`, {
      start_time: startDate,
      end_time: endDate,
      page: page,
      page_size: pageSize,
      ...filterObject,
      review_label,
      joint_output,
      reason,
      feedback,
    })
  },

  evaluateProject: async ({ start_date, end_date, project_uuid }: ProjectTimeRange): Promise<EvaluateResult> => {
    const { data } = await apiClient.postResource(backend, `/projects/${project_uuid}/evaluate/`, {
      start_time: start_date,
      end_time: end_date,
    })
    return data
  },

  evaluateModel: async ({ start_date, end_date, model_uuid }: ModelTimeRange): Promise<EvaluateResult> => {
    const { data } = await apiClient.postResource(backend, `/projects/models/${model_uuid}/evaluate/`, {
      start_time: start_date,
      end_time: end_date,
    })
    return data
  },

  getRequestDates: async (project_uuid: string): Promise<string[]> => {
    const { data } = await apiClient.fetchResource(backend, `/projects/${project_uuid}/requests/dates/`)
    return data
  },

  fetchModels: async (props: {
    projectId: string
    ordering?: "-created_at" | "name" | "state"
    page?: number
    pageSize?: number
    search?: string
    state?: ("live" | "challenger" | "disabled" | "pending-action")[]
    type?: ("custom" | "prebuilt" | "automl")[]
  }): Promise<AxiosResponse<PaginatedModelListCreateList>> => {
    const { projectId, ordering, page, pageSize, search, state, type } = props

    return apiClient.fetchResource(backend, `/projects/${projectId}/models/`, {
      ordering,
      page,
      page_size: pageSize,
      search,
      state,
      type,
    })
  },

  fetchModel: async (uuid: string): Promise<AxiosResponse<Model>> => {
    return apiClient.fetchResource(backend, `/projects/models/${uuid}/`)
  },

  createModel: async (requestBody: CreateModelRequest): Promise<AxiosResponse<CreateModelResponse>> => {
    return apiClient.postResource(backend, `/projects/${requestBody.project_uuid}/models/`, requestBody)
  },

  deleteModel: async (props: DeleteModelRequest): Promise<AxiosResponse> => {
    return apiClient.deleteResource(backend, `/projects/models/${props.model_uuid}/`)
  },

  fetchModelCpuAndRamUsage: async (
    modelUuid: string,
    start_date: string,
    end_date: string,
  ): Promise<AxiosResponse<DeploymentUsage>> => {
    return apiClient.fetchResource(backend, `/projects/models/${modelUuid}/usage/`, {
      start_time: start_date,
      end_time: end_date,
      step_size_seconds: 260,
      window_size_seconds: 60,
    })
  },

  fetchProjectCpuAndRamUsage: async (
    project_uuid: string,
    start_date: string,
    end_date: string,
  ): Promise<AxiosResponse<DeploymentUsage>> => {
    return apiClient.fetchResource(backend, `/projects/${project_uuid}/usage/`, {
      start_time: start_date,
      end_time: end_date,
      step_size_seconds: 500,
      window_size_seconds: 2500,
    })
  },

  switchModelToLive: async ({
    project_uuid,
    model_uuid,
    switch_to,
  }: {
    project_uuid: string
    model_uuid: string
    switch_to: "disabled" | "challenger"
  }): Promise<AxiosResponse> => {
    return apiClient.postResource(backend, `/projects/${project_uuid}/switch/`, {
      switch_to: switch_to,
      new_live_model: model_uuid,
    })
  },

  getProjectHistory: async (project_uuid: string): Promise<PaginatedListModelStateSwitchList> => {
    const { data } = await apiClient.fetchResource(backend, `/projects/${project_uuid}/switch/history/`)
    return data
  },

  getModelHistory: async (model_uuid: string): Promise<PaginatedListModelStateSwitchList> => {
    const { data } = await apiClient.fetchResource(backend, `/projects/models/${model_uuid}/switch/history/`)
    return data
  },

  fetchApiRequestsSummary: async ({
    start_date,
    end_date,
    project_uuid,
  }: ProjectTimeRange): Promise<AxiosResponse<DeploymentPredictionsSummary>> => {
    return apiClient.fetchResource(backend, `/projects/${project_uuid}/predictions/summary/`, {
      start_time: start_date,
      end_time: end_date,
    })
  },

  fetchLastFeedback: async (project_uuid: string): Promise<AxiosResponse<DeploymentLatestFeedbackDateTime>> => {
    return apiClient.fetchResource(backend, `/projects/${project_uuid}/predictions/feedback/latest/`)
  },

  createFeedback: async (
    props: BulkCreateFeedbackRequest & { project_uuid: string },
  ): Promise<AxiosResponse<BulkCreateFeedbackResponse>> => {
    const { project_uuid, feedback } = props

    return apiClient.postResource(backend, `/projects/${project_uuid}/predictions/feedback/`, {
      feedback,
    })
  },

  updateFeedback: async (
    props: {
      prediction_uuid: string
    } & PatchedUpdateFeedbackRequest,
  ): Promise<AxiosResponse<UpdateFeedback>> => {
    const { prediction_uuid, target } = props
    return apiClient.patchResource(backend, `/projects/predictions/${prediction_uuid}/feedback/`, {
      target,
    })
  },

  fetchProjectRequestLogData: async (
    startDate: string,
    endDate: string,
    projectId: string,
    page: number,
    pageSize: number,
  ): Promise<AxiosResponse<PaginatedListPredictionsList>> => {
    return apiClient.fetchResource(backend, `/projects/${projectId}/predictions/`, {
      start_time: startDate,
      end_time: endDate,
      page: page,
      page_size: pageSize,
    })
  },

  fetchModelRequestLogData: async (
    startDate: string,
    endDate: string,
    modelUuid: string,
    page?: number,
    pageSize?: number,
  ): Promise<AxiosResponse<PaginatedListModelOutputsList>> => {
    return apiClient.fetchResource(backend, `/projects/models/${modelUuid}/predictions/`, {
      start_time: startDate,
      end_time: endDate,
      page: page,
      page_size: pageSize,
    })
  },

  fetchProjectsPredictionsNextRun: async (project_uuid: string): Promise<DeploymentPredictionNextRunAt> => {
    const { data } = await apiClient.fetchResource(backend, `/projects/models/${project_uuid}/predictions/next-run-at/`)
    return data
  },

  fetchModelClassificationEvaluationMetrics: async (
    startDate: string,
    endDate: string,
    modelUuid: string,
  ): Promise<AxiosResponse<ClassificationEvaluation>> => {
    return apiClient.fetchResource(backend, `/projects/models/${modelUuid}/evaluate/classification/`, {
      start_time: startDate,
      end_time: endDate,
    })
  },

  fetchModelOutputPredictionsData: async (
    startDate: string,
    endDate: string,
    modelUuid: string,
    page?: number,
    pageSize?: number,
    filterObject?: object,
  ): Promise<AxiosResponse<PaginatedListClassificationModelOutputsList>> => {
    return apiClient.fetchResource(backend, `/projects/models/${modelUuid}/predictions/classification/`, {
      start_time: startDate,
      end_time: endDate,
      page: page,
      page_size: pageSize,
      ...filterObject,
    })
  },

  fetchProjectPredictionDates: async (projectId: string): Promise<AxiosResponse> => {
    return apiClient.fetchResource(backend, `/projects/${projectId}/predictions/dates/`)
  },

  createRetrainingModel: async (requestBody: CreateRetrainingModelRequest): Promise<AxiosResponse> => {
    return apiClient.postResource(backend, `/projects/${requestBody.project_uuid}/retraining/`, {
      predictions_start_time: requestBody.predictions_start_time,
      predictions_end_time: requestBody.predictions_end_time,
      append_training_data: requestBody.append_training_data,
      resulting_model_name: requestBody.resulting_model_name,
      resulting_model_state: requestBody.resulting_model_state,
    })
  },

  fetchRetrainingModels: async (
    project_uuid: string,
    pageSize?: number,
    page?: number,
  ): Promise<PaginatedRetrainingJobList> => {
    const { data } = await apiClient.fetchResource(backend, `/projects/${project_uuid}/retraining/`, {
      page_size: pageSize,
      page: page,
    })
    return data
  },

  fetchTrainingModels: async (
    project_uuid: string,
    pageSize?: number,
    page?: number,
  ): Promise<PaginatedTrainingJobList> => {
    const { data } = await apiClient.fetchResource(backend, `/projects/${project_uuid}/training/`, {
      page_size: pageSize,
      page: page,
    })
    return data
  },

  fetchRetrainingModelLogs: async (project_uuid: string, uuid: string): Promise<RetrainingJobLogs> => {
    const { data } = await apiClient.fetchResource(backend, `/projects/${project_uuid}/retraining/${uuid}/logs/`)
    return data
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchTrainingModelLogs: async (project_uuid: string, uuid: string): Promise<any> => {
    const { data } = await apiClient.fetchResource(backend, `/projects/${project_uuid}/training/${uuid}/logs/`)
    return data
  },

  fetchAutoMLTrainingJobLogs: async (project_uuid: string, uuid: string): Promise<AutoMLJobLogs> => {
    const { data } = await apiClient.fetchResource(backend, `/projects/${project_uuid}/automl/trainings/${uuid}/logs/`)
    return data
  },

  deleteRetrainingModel: async (project_uuid: string): Promise<AxiosResponse> => {
    return apiClient.deleteResource(backend, `/projects/${project_uuid}/retraining/`)
  },

  deleteTrainingModel: async (project_uuid: string, training_uuid: string): Promise<AxiosResponse> => {
    return apiClient.deleteResource(backend, `/projects/${project_uuid}/training/${training_uuid}`)
  },

  createIssue: async ({ summary, issueType, description }: CreateIssueRequest): Promise<AxiosResponse> => {
    return apiClient.postResource(backend, `/support/`, {
      summary: summary,
      issue_type: issueType,
      description: description,
    })
  },

  fetchModelConfigurationsList: async (
    model_uuid: string,
    page?: number,
    pageSize?: number,
  ): Promise<AxiosResponse<PaginatedModelSettingsSetList>> => {
    return apiClient.fetchResource(backend, `/projects/models/${model_uuid}/configurations/`, {
      page: page,
      page_size: pageSize,
    })
  },

  fetchActiveModelConfiguration: async (model_uuid: string): Promise<AxiosResponse<ListClassificationModelOutputs>> => {
    return apiClient.fetchResource(backend, `/projects/models/${model_uuid}/configurations/active/`)
  },

  createModelConfiguration: async ({
    model,
    type,
    target_key_path,
    settings,
    auto_add,
  }: ModelSettingsSetRequest): Promise<AxiosResponse<ModelSettingsSet>> => {
    return apiClient.postResource(backend, `/projects/models/${model}/configurations/`, {
      model,
      type,
      target_key_path,
      settings,
      auto_add,
    })
  },

  fetchRequiredSchema: async (projectId: string): Promise<RequiredSchema[]> => {
    const { data } = await apiClient.fetchResource(backend, `/projects/${projectId}/prebuilt-model-schema/`)
    return data
  },

  fetchProjectLimits: async (): Promise<AxiosResponse<ProjectLimits>> => {
    return apiClient.fetchResource(backend, `/projects/limits/`)
  },

  trainModelUsingPrebuiltModel: async (props: TrainModelUsingPrebuiltModelRequest): Promise<AxiosResponse> => {
    return apiClient.postResource(backend, `/projects/${props.projectId}/models/prebuilt/`, {
      name: props.modelName,
      training_data: props.trainingDataId,
    })
  },

  fetchRulesets: async (
    projectId: string,
    page?: number,
    page_size?: number,
    search?: string,
    ordering?: string,
  ): Promise<AxiosResponse<PaginatedRuleSetListList>> => {
    return apiClient.fetchResource(backend, `/projects/${projectId}/rulesets/`, {
      page,
      page_size,
      search,
      ordering,
    })
  },

  createRuleset: async (props: CreateRulesetRequest): Promise<AxiosResponse> => {
    return apiClient.postResource(backend, `/projects/${props.projectId}/rulesets/`, {
      rules: props?.rules,
      name: props.name,
      type: props.type,
    })
  },

  updateRuleset: async (props: UpdateRulesetRequest): Promise<AxiosResponse> => {
    return apiClient.patchResource(backend, `/projects/${props.projectId}/rulesets/${props.rulesetId}/`, {
      rules: props.rules,
      name: props.name,
    })
  },

  fetchRuleset: async (projectId: string, rulesetId: string): Promise<AxiosResponse<RuleSetGroupRetrieve>> => {
    return apiClient.fetchResource(backend, `/projects/${projectId}/rulesets/${rulesetId}/`)
  },

  listRulesetVersions: async (props: {
    projectId: string
    rulesetId: string
    page?: number
    page_size?: number
  }): Promise<AxiosResponse<PaginatedRuleSetVersionsListList>> => {
    const { projectId, rulesetId, page, page_size } = props

    return apiClient.fetchResource(backend, `/projects/${projectId}/rulesets/${rulesetId}/versions`, {
      page: page,
      page_size: page_size,
    })
  },

  retrieveRulesetVersion: async (props: {
    projectId: string
    rulesetId: string
    version: string
  }): Promise<AxiosResponse<RuleSetVersionRetrieve>> => {
    const { projectId, rulesetId, version } = props

    return apiClient.fetchResource(backend, `/projects/${projectId}/rulesets/${rulesetId}/versions/${version}`)
  },

  deleteWorkflowResource: async (props: {
    project_uuid: string
    resource_uuid: string
    resource_type: "rulesets" | "taglists" | "programs" | "scorecardsets" | "scorecards" | "calculators"
  }): Promise<AxiosResponse> => {
    const { project_uuid, resource_uuid, resource_type } = props

    return apiClient.deleteResource(backend, `/projects/${project_uuid}/${resource_type}/${resource_uuid}/`)
  },

  fetchWorkflows: async ({
    projectId,
    page_size,
    page,
    search,
  }: {
    projectId: string
    page_size: number
    page?: number
    search?: string
  }): Promise<AxiosResponse<PaginatedWorkflowListList>> => {
    return apiClient.fetchResource(backend, `/projects/${projectId}/workflows/`, {
      page_size: page_size,
      page,
      search,
    })
  },

  fetchWorkflowVersions: async ({
    projectId,
    workflowUUID,
    pageSize,
    page,
  }: {
    projectId: string
    workflowUUID: string
    pageSize?: number
    page?: number
  }): Promise<AxiosResponse<PaginatedWorkflowVersionListList>> => {
    return apiClient.fetchResource(backend, `/projects/${projectId}/workflows/${workflowUUID}/versions/`, {
      page_size: pageSize,
      page,
    })
  },

  fetchWorkflowVersion: async (
    projectId: string,
    version: string,
    workflowUUID: string,
  ): Promise<AxiosResponse<WorkflowVersionRetrieve>> => {
    // adding the extra ".0" since the backend requires the version to be X.Y at a minimum
    // where X is the major version and Y is the minor version
    const computedVersion = version.split(".").length === 2 ? version : version + ".0"

    return apiClient.fetchResource(
      backend,
      `/projects/${projectId}/workflows/${workflowUUID}/versions/${computedVersion}/`,
    )
  },

  fetchWorkflow: async (projectId: string, workflowUUID: string): Promise<AxiosResponse<WorkflowGroupRetrieve>> => {
    return apiClient.fetchResource(backend, `/projects/${projectId}/workflows/${workflowUUID}/`)
  },

  createWorkflow: async (props: CreateWorkflowRequest): Promise<AxiosResponse> => {
    const { projectId, name, state, start_node, nodes, schema, constants } = props

    return apiClient.postResource(backend, `/projects/${projectId}/workflows/`, {
      name: name,
      state: state,
      start_node: start_node,
      nodes: nodes,
      schema: schema,
      constants: constants,
    })
  },
  // update a workflow
  updateWorkflow: async (props: UpdateWorkflowRequest): Promise<AxiosResponse> => {
    const {
      projectId,
      workflowUUID,
      deprecate_workflows = false,
      name,
      newState,
      nodes,
      schema,
      start_node,
      constants,
    } = props
    return apiClient.patchResource(
      backend,
      `/projects/${projectId}/workflows/${workflowUUID}/?deprecate_workflows=${deprecate_workflows}`,
      {
        state: newState,
        name: name,
        nodes: nodes,
        start_node: start_node,
        schema: schema,
        constants: constants,
      },
    )
  },
  // update scorecard-set
  updateScorecardset: async (
    props: UpdateScorecardsetRequest,
  ): Promise<AxiosResponse<PatchedScoreCardSetCreateUpdateRequest>> => {
    return apiClient.patchResource(backend, `/projects/${props.projectId}/scorecardsets/${props.scorecardsetId}/`, {
      name: props.name,
      calculation_method: props.calculation_method,
      scorecards: props?.scorecards,
    })
  },

  updateScorecard: async (props: UpdateScorecardRequest): Promise<AxiosResponse<ScoreCardCreateUpdate>> => {
    const { projectId, scorecardId } = props

    return apiClient.patchResource(backend, `/projects/${projectId}/scorecards/${scorecardId}/`, {
      ...props,
    })
  },
  // fetch scorecard-sets
  fetchScorecardsets: async (props: {
    projectId: string
    page?: number
    pageSize?: number
    search?: string
    ordering?: "created_at" | "-created_at" | "last_used" | "-last_used"
  }): Promise<AxiosResponse<PaginatedScoreCardSetListList>> => {
    const { projectId, page, pageSize, search, ordering } = props

    return apiClient.fetchResource(backend, `/projects/${projectId}/scorecardsets/`, {
      page_size: pageSize,
      page,
      search,
      ordering,
    })
  },

  createScorecard: async (props: CreateScoreCardRequest): Promise<AxiosResponse> => {
    const { projectId } = props
    return apiClient.postResource(backend, `/projects/${projectId}/scorecards/`, {
      ...props,
    })
  },

  createScorecardSet: async (props: CreateScorecardSetRequest): Promise<AxiosResponse> => {
    return apiClient.postResource(backend, `/projects/${props.projectId}/scorecardsets/`, {
      name: props.name,
      calculation_method: props.calculation_method,
      scorecards: props.scorecards,
    })
  },

  fetchScorecardset: async (
    projectId: string,
    scorecardsetId: string,
  ): Promise<AxiosResponse<ScoreCardSetGroupRetrieve>> => {
    return apiClient.fetchResource(backend, `/projects/${projectId}/scorecardsets/${scorecardsetId}/`)
  },

  listScorecardSetVersions: async (props: {
    projectId: string
    scorecardsetId: string
    page?: number
    page_size?: number
  }): Promise<AxiosResponse<PaginatedScoreCardSetVersionsListList>> => {
    const { projectId, scorecardsetId, page, page_size } = props

    return apiClient.fetchResource(backend, `/projects/${projectId}/scorecardsets/${scorecardsetId}/versions`, {
      page: page,
      page_size: page_size,
    })
  },

  retrieveScorecardSetVersion: async (props: {
    projectId: string
    scorecardsetId: string
    version: string
  }): Promise<AxiosResponse<ScoreCardSetRetrieve>> => {
    const { projectId, scorecardsetId, version } = props

    return apiClient.fetchResource(
      backend,
      `/projects/${projectId}/scorecardsets/${scorecardsetId}/versions/${version}`,
    )
  },

  fetchScorecards: async (
    projectId: string,
    page?: number,
    pageSize?: number,
    search?: string,
    ordering?: string,
  ): Promise<AxiosResponse<PaginatedScoreCardListList>> => {
    return apiClient.fetchResource(backend, `/projects/${projectId}/scorecards/`, {
      page_size: pageSize,
      page,
      search,
      ordering,
    })
  },

  fetchScorecard: async (projectId: string, scorecardId: string): Promise<AxiosResponse<ScoreCardGroupRetrieve>> => {
    return apiClient.fetchResource(backend, `/projects/${projectId}/scorecards/${scorecardId}`)
  },

  listScorecardVersions: async (props: {
    projectId: string
    scorecardId: string
    page?: number
    page_size?: number
  }): Promise<AxiosResponse<PaginatedScoreCardVersionsListList>> => {
    const { projectId, scorecardId, page, page_size } = props

    return apiClient.fetchResource(backend, `/projects/${projectId}/scorecards/${scorecardId}/versions`, {
      page: page,
      page_size: page_size,
    })
  },

  retrieveScorecardVersion: async (props: {
    projectId: string
    scorecardId: string
    version: string
  }): Promise<AxiosResponse<ScoreCardRetrieve>> => {
    const { projectId, scorecardId, version } = props

    return apiClient.fetchResource(backend, `/projects/${projectId}/scorecards/${scorecardId}/versions/${version}`)
  },
  // Programs
  fetchProgram: async (projectId: string, programId: string): Promise<AxiosResponse<ProgramRetrieve>> => {
    return apiClient.fetchResource(backend, `/projects/${projectId}/programs/${programId}`)
  },

  listProgramVersions: async (props: {
    projectId: string
    programId: string
    page?: number
    page_size?: number
  }): Promise<AxiosResponse<PaginatedProgramVersionListList>> => {
    const { projectId, programId, page, page_size } = props

    return apiClient.fetchResource(backend, `/projects/${projectId}/programs/${programId}/versions`, {
      page: page,
      page_size: page_size,
    })
  },

  retrieveProgramVersion: async (props: {
    projectId: string
    programId: string
    version: string
  }): Promise<AxiosResponse<ProgramVersionRetrieve>> => {
    const { projectId, programId, version } = props

    return apiClient.fetchResource(backend, `/projects/${projectId}/programs/${programId}/versions/${version}`)
  },

  fetchPrograms: async (
    projectId: string,
    page?: number,
    page_size?: number,
    search?: string,
    ordering?: string,
  ): Promise<AxiosResponse<PaginatedProgramList>> => {
    return apiClient.fetchResource(backend, `/projects/${projectId}/programs/`, {
      page: page,
      page_size: page_size,
      search,
      ordering,
    })
  },

  createProgram: async (props: CreateProgramRequest): Promise<AxiosResponse<Program>> => {
    return apiClient.postResource(backend, `/projects/${props.projectId}/programs/`, {
      name: props.name,
      items: props?.items,
    })
  },

  updateProgram: async (props: UpdateProgramRequest): Promise<AxiosResponse> => {
    return apiClient.patchResource(backend, `/projects/${props.projectId}/programs/${props.programId}/`, {
      name: props.name,
      items: props?.items,
    })
  },

  trainModelUsingAutoMlModel: async (props: CreateAutoMlRequest): Promise<AxiosResponse> => {
    const { excluded_columns, name, training_data, target_column, mapping } = props
    return apiClient.postResource(backend, `/projects/${props.projectId}/automl/trainings/`, {
      name,
      mapping,
      excluded_columns,
      target_column,
      training_data,
    })
  },

  fetchTrainingDataSchema: async (projectId: string, trainingDataId: string): Promise<TrainingDataSchemaResponse> => {
    const { data } = await apiClient.fetchResource(
      backend,
      `/projects/${projectId}/training-data/${trainingDataId}/schema/`,
    )
    return data
  },
  // get all pre-defined types for automl mapping
  fetchAutomlPredefinedTypes: async (props: {
    projectId: string
    type: string
  }): Promise<AxiosResponse<PaginatedPredefinedTypeListList>> => {
    const { projectId, type } = props
    return apiClient.fetchResource(backend, `/projects/${projectId}/automl/predefined-types/`, {
      type,
    })
  },

  fetchAutoMlTrainingJobs: async (
    project_uuid: string,
    pageSize?: number,
    page?: number,
  ): Promise<PaginatedAutoMLTrainingJobList> => {
    const { data } = await apiClient.fetchResource(backend, `/projects/${project_uuid}/automl/trainings/`, {
      page_size: pageSize,
      page: page,
    })
    return data
  },
  configureAutoMlModel: async (
    props: ConfigureAutoMlModelRequest,
  ): Promise<AxiosResponse<PatchedConfigureAutoMLRequest>> => {
    const { lower_bound, upper_bound, state, negative_label, positive_label } = props
    return apiClient.patchResource(backend, `/projects/${props.projectId}/automl/${props.modelUUID}/configure/`, {
      lower_bound,
      upper_bound,
      state,
      negative_label,
      positive_label,
    })
  },
  // list all thresholds with their metrics
  fetchThresholdWithMetrics: async (props: { projectId: string; modelUUID: string }): Promise<AxiosResponse> => {
    const { modelUUID, projectId } = props
    return apiClient.fetchResource(backend, `/projects/${projectId}/automl/${modelUUID}/threshold-metrics/`)
  },
  // DataFiles
  uploadDataFile: async (props: UploadDataFileRequest): Promise<DataFileGroup> => {
    const { project_uuid, file, setProgress, signal, type, name } = props

    const formData = new FormData()
    formData.append("data_file", file)
    formData.append("type", type)
    formData.append("name", name ?? file.name)
    const { data } = await apiClient.postResource(backend, `/projects/${project_uuid}/data-files/upload/`, formData, {
      onUploadProgress: function (progressEvent) {
        // TODO:: check api later
        progressEvent.total && setProgress?.((progressEvent.loaded / progressEvent.total) * 100)
      },
      signal,
    })
    return data
  },
  // DataFiles
  updateDataFile: async (props: UpdateDataFileRequest): Promise<DataFileGroup> => {
    const { project_uuid, file, setProgress, signal, data_file_uuid } = props

    const formData = new FormData()
    formData.append("data_file", file)
    const { data } = await apiClient.putResource(
      backend,
      `/projects/${project_uuid}/data-files/${data_file_uuid}/update/`,
      formData,
      {
        onUploadProgress: function (progressEvent) {
          // TODO:: check api later
          progressEvent.total && setProgress?.((progressEvent.loaded / progressEvent.total) * 100)
        },
        signal,
      },
    )

    return data
  },

  cloneDataFiles: async (props: CloneDataFilesRequest): Promise<AxiosResponse<DataFileCloneResponse[]>> => {
    const { project_uuid, data_files } = props

    return apiClient.postResource(backend, `/projects/${project_uuid}/data-files/import/`, { source_files: data_files })
  },
  fetchDataFiles: async (props: {
    project_uuid: string
    pageSize?: number
    page?: number
    search?: string
    ordering?: "created_at" | "-created_at" | "name" | "-name"
    type?: string[]
  }): Promise<AxiosResponse<PaginatedDataFileGroupListList>> => {
    const { project_uuid, type, pageSize, page, search, ordering } = props

    return apiClient.fetchResource(backend, `/projects/${project_uuid}/data-files/`, {
      type: type,
      page_size: pageSize,
      page: page,
      search: search,
      ordering: ordering,
    })
  },
  fetchDataFileVersions: async (props: {
    project_uuid: string
    data_file_uuid: string
    pageSize?: number
    page?: number
  }): Promise<AxiosResponse<PaginatedDataFileVersionsListList>> => {
    const { project_uuid, pageSize, page, data_file_uuid } = props

    return apiClient.fetchResource(backend, `/projects/${project_uuid}/data-files/${data_file_uuid}/versions/`, {
      page_size: pageSize,
      page: page,
    })
  },
  fetchDataFile: async (project_uuid: string, file_uuid: string): Promise<AxiosResponse<DataFileGroup>> => {
    return apiClient.fetchResource(backend, `/projects/${project_uuid}/data-files/${file_uuid}/`)
  },
  fetchDataFileVersion: async (
    project_uuid: string,
    file_uuid: string,
    version: string,
  ): Promise<AxiosResponse<DataFile>> => {
    return apiClient.fetchResource(backend, `/projects/${project_uuid}/data-files/${file_uuid}/versions/${version}/`)
  },

  deleteDataFile: async (project_uuid: string, data_file_uuid: string): Promise<AxiosResponse> => {
    return apiClient.deleteResource(backend, `/projects/${project_uuid}/data-files/${data_file_uuid}/`)
  },

  // Simulation
  createSimulation: async (
    props: WorkflowSimulationJobCreateRequest,
    workflowId: string,
  ): Promise<AxiosResponse<WorkflowSimulationJobCreate>> => {
    const { projectId, predictions_datafile, predictions_start_time, predictions_end_time, feedback_column } = props

    return apiClient.postResource(backend, `/projects/${projectId}/workflows/simulations/`, {
      predictions_datafile: predictions_datafile,
      predictions_start_time: predictions_start_time,
      predictions_end_time: predictions_end_time,
      feedback_column: feedback_column,
      workflow: workflowId,
    })
  },

  // Simulation
  cancelSimulation: async (props: {
    projectId: string
    simulation_uuid: string
  }): Promise<AxiosResponse<WorkflowSimulationJobCreate>> => {
    const { projectId, simulation_uuid } = props

    return apiClient.postResource(
      backend,
      `/projects/${projectId}/workflows/simulations/${simulation_uuid}/cancel/`,
      {},
    )
  },

  fetchSimulations: async (
    props: WorkflowSimulationJobFetchRequest,
  ): Promise<AxiosResponse<PaginatedSimulationJobRetrieveList>> => {
    const { projectId, page, pageSize, workflow_name } = props

    return apiClient.fetchResource(backend, `/projects/${projectId}/workflows/simulations/`, {
      page: page,
      page_size: pageSize,
      workflow_name: workflow_name,
    })
  },

  fetchSimulationOutputs: async (
    props: WorkflowSimulationOutputFetchRequest,
  ): Promise<AxiosResponse<PaginatedWorkflowSimulationOutputList>> => {
    const { projectId, simulation_uuid, page, pageSize, filterObject } = props

    return apiClient.fetchResource(backend, `/projects/${projectId}/workflows/simulations/${simulation_uuid}/outputs`, {
      page: page,
      page_size: pageSize,
      ...filterObject,
    })
  },
  // Labels
  RetrieveLabels: async (props: RetrieveLabelsRequest): Promise<AxiosResponse<PaginatedLabelList>> => {
    const { project_uuid, page, pageSize, search } = props

    return await apiClient.fetchResource(backend, `/projects/${project_uuid}/labels/`, {
      page,
      search,
      page_size: pageSize,
    })
  },

  FetchLabel: async (props: { project_uuid: string; label_uuid: string }): Promise<AxiosResponse<Label>> => {
    const { project_uuid, label_uuid } = props

    return await apiClient.fetchResource(backend, `/projects/${project_uuid}/labels/${label_uuid}/`)
  },

  CreateLabel: async (props: { project_uuid: string; name: string }): Promise<AxiosResponse<Label>> => {
    const { project_uuid, name } = props

    return await apiClient.postResource(backend, `/projects/${project_uuid}/labels/`, { name: name })
  },

  BulkCreateLabels: async (props: {
    project_uuid: string
    labelsNames: { name: string }[]
  }): Promise<AxiosResponse<Label[]>> => {
    const { project_uuid, labelsNames } = props

    return await apiClient.postResource(backend, `/projects/${project_uuid}/labels/bulk/`, labelsNames)
  },

  DeleteLabel: async (props: { project_uuid: string; label_uuid: string }): Promise<AxiosResponse> => {
    const { project_uuid, label_uuid } = props

    return await apiClient.deleteResource(backend, `/projects/${project_uuid}/labels/${label_uuid}/`)
  },
  // Output filter
  createUpdateOutputFilters: async (props: {
    project_uuid: string
    workflow_uuid: string
    fields: string[]
  }): Promise<AxiosResponse<WorkflowResponseFilter>> => {
    const { project_uuid, workflow_uuid, fields } = props

    return apiClient.putResource(backend, `/projects/${project_uuid}/workflows/${workflow_uuid}/response-filter/`, {
      fields: fields,
    })
  },
  // scripts
  fetchScripts: async (
    projectId: string,
    page?: number,
    page_size?: number,
    search?: string,
    is_builtin?: boolean,
    status?: (keyof typeof StatusTextMap)[],
    ordering?: "-created_at" | "name",
  ): Promise<AxiosResponse<PaginatedScriptGroupListList>> => {
    return apiClient.fetchResource(backend, `/projects/${projectId}/scripts/`, {
      page: page,
      page_size: page_size ?? 200,
      search,
      is_builtin,
      status,
      ordering,
    })
  },

  fetchScript: async (props: { projectId: string; scriptId: string }): Promise<AxiosResponse<ScriptGroupRetrieve>> => {
    const { projectId, scriptId } = props

    return apiClient.fetchResource(backend, `/projects/${projectId}/scripts/${scriptId}/`)
  },

  listScriptVersions: async (props: {
    projectId: string
    scriptId: string
    page?: number
    page_size?: number
  }): Promise<AxiosResponse<PaginatedScriptVersionList>> => {
    const { projectId, scriptId, page, page_size } = props

    return apiClient.fetchResource(backend, `/projects/${projectId}/scripts/${scriptId}/versions`, {
      page: page,
      page_size: page_size,
    })
  },

  retrieveScriptVersion: async (props: {
    projectId: string
    scriptId: string
    version: string
  }): Promise<AxiosResponse<ScriptVersionRetrieve>> => {
    const { projectId, scriptId, version } = props

    return apiClient.fetchResource(backend, `/projects/${projectId}/scripts/${scriptId}/versions/${version}`)
  },
  // create script
  createScript: async (props: CreateScriptRequest): Promise<AxiosResponse<ScriptGroupRetrieve>> => {
    const { file, name, project_uuid, is_draft, language, requirements_file, creds } = props

    return apiClient.postResource(backend, `/projects/${project_uuid}/scripts/`, {
      file,
      name,
      is_draft,
      language,
      requirements_file,
      creds,
    })
  },
  // update
  updateScript: async (props: UpdateScriptRequest): Promise<AxiosResponse<ScriptGroupRetrieve>> => {
    const { file, name, project_uuid, is_draft, language, script_uuid, creds, requirements_file } = props

    return apiClient.patchResource(backend, `/projects/${project_uuid}/scripts/${script_uuid}/`, {
      file,
      name,
      is_draft,
      language,
      creds,
      requirements_file,
    })
  },
  // script-logs
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchScriptLogs: async ({ start_date, end_date, project_uuid, script_uuid }: ScriptLogsRange): Promise<any> => {
    const { data } = await apiClient.fetchResource(backend, `/projects/${project_uuid}/scripts/${script_uuid}/logs/`, {
      start_time: start_date,
      end_time: end_date,
    })
    return data
  },

  DeleteScript: async (props: { project_uuid: string; script_uuid: string }): Promise<AxiosResponse> => {
    const { project_uuid, script_uuid } = props

    return await apiClient.deleteResource(backend, `/projects/${project_uuid}/scripts/${script_uuid}/`)
  },

  deleteScriptCreds: async (props: DeleteScriptCredsRequest): Promise<AxiosResponse> => {
    const { project_uuid, script_uuid, creds_keys } = props

    return await apiClient.postResource(backend, `/projects/${project_uuid}/scripts/${script_uuid}/delete-creds/`, {
      creds_keys,
    })
  },
  // fetch templates (ready scripts)
  fetchReadyTemplates: async (props: {
    projectId: string
    page?: number
    page_size?: number
    search?: string
    language?: string
  }): Promise<AxiosResponse<PaginatedScriptTemplateList>> => {
    const { projectId, language, page, page_size, search } = props
    return apiClient.fetchResource(backend, `/projects/${projectId}/scripts/templates/`, {
      page,
      page_size,
      search,
      language,
    })
  },
  // fetch builtin template (ready script)
  fetchReadyTemplate: async (props: {
    projectId: string
    templateId: string
  }): Promise<AxiosResponse<ScriptTemplateRetrieve>> => {
    const { projectId, templateId } = props
    return apiClient.fetchResource(backend, `/projects/${projectId}/scripts/templates/${templateId}/`)
  },
  // create built-in script
  createBuiltinScript: async (props: CreateBuiltinScriptRequest): Promise<AxiosResponse> => {
    const { project_uuid, template_uuid, creds, name } = props

    return apiClient.postResource(backend, `/projects/${project_uuid}/scripts/from-template/${template_uuid}/`, {
      creds,
      name,
    })
  },
  // test
  testScript: async (props: TestScriptRequest): Promise<AxiosResponse<ScriptTestOutput>> => {
    const { project_uuid, script_uuid, features, version } = props

    return apiClient.postResource(backend, `/projects/${project_uuid}/scripts/${script_uuid}/test/`, {
      ...features,
      version,
    })
  },
  // cancel building script
  cancelBuildingScript: async (props: CancelBuildingScriptRequest): Promise<AxiosResponse> => {
    const { project_uuid, script_uuid, version } = props

    return apiClient.deleteResource(
      backend,
      `/projects/${project_uuid}/scripts/${script_uuid}/versions/${version}/cancel/`,
    )
  },
  // Calculators
  listCalculators: async (props: {
    projectId: string
    page?: number
    page_size?: number
    search?: string
    ordering?: string
  }): Promise<AxiosResponse<PaginatedCalculatorListList>> => {
    const { projectId, page, page_size, search, ordering } = props

    return apiClient.fetchResource(backend, `/projects/${projectId}/calculators/`, {
      page,
      page_size,
      search,
      ordering,
    })
  },

  retrieveCalculator: async (props: {
    projectId: string
    calculatorId: string
  }): Promise<AxiosResponse<CalculatorGroupRetrieve>> => {
    const { projectId, calculatorId } = props
    return apiClient.fetchResource(backend, `/projects/${projectId}/calculators/${calculatorId}/`)
  },

  listCalculatorVersions: async (props: {
    projectId: string
    calculatorId: string
    page?: number
    page_size?: number
  }): Promise<AxiosResponse<PaginatedCalculatorVersionRetrieveList>> => {
    const { projectId, calculatorId, page, page_size } = props

    return apiClient.fetchResource(backend, `/projects/${projectId}/calculators/${calculatorId}/versions`, {
      page: page,
      page_size: page_size,
    })
  },

  retrieveCalculatorVersion: async (props: {
    projectId: string
    calculatorId: string
    version: string
  }): Promise<AxiosResponse<CalculatorVersionRetrieve>> => {
    const { projectId, calculatorId, version } = props

    return apiClient.fetchResource(backend, `/projects/${projectId}/calculators/${calculatorId}/versions/${version}`)
  },

  createCalculator: async (props: CalculatorCreateRequest & { projectId: string }): Promise<AxiosResponse> => {
    const { projectId, equations, name } = props

    return apiClient.postResource(backend, `/projects/${projectId}/calculators/`, { equations, name })
  },

  updateCalculator: async (
    props: PatchedCalculatorUpdateRequest & { projectId: string; calculatorId: string },
  ): Promise<AxiosResponse> => {
    const { projectId, calculatorId, equations, name } = props

    return apiClient.patchResource(backend, `/projects/${projectId}/calculators/${calculatorId}/`, { equations, name })
  },
  // TagLists
  fetchTagLists: async (
    projectId: string,
    page?: number,
    page_size?: number,
    search?: string,
    ordering?: string,
  ): Promise<AxiosResponse<PaginatedTagListListList>> => {
    return apiClient.fetchResource(backend, `/projects/${projectId}/taglists/`, {
      page,
      page_size,
      search,
      ordering,
    })
  },

  fetchTaglist: async (projectId: string, taglistId: string): Promise<AxiosResponse<TagListGroupRetrieve>> => {
    return apiClient.fetchResource(backend, `/projects/${projectId}/taglists/${taglistId}/`)
  },

  listTaglistVersions: async (props: {
    projectId: string
    taglistId: string
    page?: number
    page_size?: number
  }): Promise<AxiosResponse<PaginatedTagListVersionListList>> => {
    const { projectId, taglistId, page, page_size } = props

    return apiClient.fetchResource(backend, `/projects/${projectId}/taglists/${taglistId}/versions`, {
      page: page,
      page_size: page_size,
    })
  },

  retrieveTaglistVersion: async (props: {
    projectId: string
    taglistId: string
    version: string
  }): Promise<AxiosResponse<TagListVersionRetrieve>> => {
    const { projectId, taglistId, version } = props

    return apiClient.fetchResource(backend, `/projects/${projectId}/taglists/${taglistId}/versions/${version}`)
  },

  createTaglist: async (props: CreateTaglistRequest): Promise<AxiosResponse> => {
    return apiClient.postResource(backend, `/projects/${props.projectId}/taglists/`, {
      rules: props?.rules,
      name: props.name,
    })
  },

  updateTaglist: async (props: UpdateTaglistRequest): Promise<AxiosResponse> => {
    return apiClient.patchResource(backend, `/projects/${props.projectId}/taglists/${props.taglistId}/`, {
      rules: props.rules,
      name: props.name,
    })
  },

  changeResourceVersion: async (props: VersionChangeRequest): Promise<AxiosResponse> => {
    // adding the extra ".0" since the backend requires the version to be X.Y at a minimum
    // where X is the major version and Y is the minor version
    const version = props.version.split(".").length === 2 ? props.version : props.version + ".0"

    return apiClient.patchResource(
      backend,
      `/projects/${props.projectId}/${props.resource}/${props.resourceId}/change-version/`,
      {
        version: version,
      },
    )
  },

  configureTableColumns: async (
    props: TableConfigurationRequest & { project_uuid: string },
  ): Promise<AxiosResponse<TableColumnConfigurationRequest>> => {
    const { project_uuid, column_configs, table } = props

    return apiClient.putResource(backend, `/projects/${project_uuid}/table-configurations/`, {
      column_configs,
      table,
    })
  },

  fetchTableColumnsConfigs: async (
    projectId: string,
    table: string,
  ): Promise<AxiosResponse<TableConfigurationRequest>> => {
    return apiClient.fetchResource(backend, `/projects/${projectId}/table-configurations/`, {
      table,
    })
  },
  // Export
  listExportJobs: async (props: {
    project_uuid: string
    search: string
    status: string
    page: number
    page_size: number
    ordering: string
  }): Promise<AxiosResponse<PaginatedExportJobListList>> => {
    const { project_uuid, search, status, page, page_size, ordering } = props

    return apiClient.fetchResource(backend, `/projects/${project_uuid}/export-jobs/`, {
      name: search,
      status: status,
      page: page,
      page_size: page_size,
      ordering: ordering,
    })
  },

  retrieveExportJob: async (props: {
    project_uuid: string
    export_job_uuid: string
  }): Promise<AxiosResponse<ExportJobRetrieve>> => {
    const { project_uuid, export_job_uuid } = props

    return apiClient.fetchResource(backend, `/projects/${project_uuid}/export-jobs/${export_job_uuid}/`)
  },

  createExportJob: async (
    props: ExportJobCreateRequest & CreateExportJobRequestProps,
  ): Promise<AxiosResponse<ExportJobRetrieve>> => {
    const { project_uuid } = props

    return apiClient.postResource(backend, `/projects/${project_uuid}/export-jobs/`, {
      ...props,
    })
  },

  deleteExportJob: async (props: {
    project_uuid: string
    export_job_uuid: string
  }): Promise<AxiosResponse<ExportJobRetrieve>> => {
    const { project_uuid, export_job_uuid } = props

    return apiClient.deleteResource(backend, `/projects/${project_uuid}/export-jobs/${export_job_uuid}/`)
  },

  cancelExportJob: async (props: {
    project_uuid: string
    export_job_uuid: string
  }): Promise<AxiosResponse<ExportJobRetrieve>> => {
    const { project_uuid, export_job_uuid } = props

    return apiClient.deleteResource(backend, `/projects/${project_uuid}/export-jobs/${export_job_uuid}/cancel/`)
  },

  retrieveExportJobsDownloadLogs: async (props: {
    project_uuid: string
    search: string
  }): Promise<AxiosResponse<PaginatedDownloadExportJobList>> => {
    const { project_uuid, search } = props

    return apiClient.fetchResource(backend, `/projects/${project_uuid}/downloaded-export-jobs/`, {
      export_job_name: search,
    })
  },
  // Prediction Reviews
  createPredictionReview: async (
    props: PredictionReviewRequest & { prediction_uuid: string },
  ): Promise<AxiosResponse<PredictionReview>> => {
    const { prediction_uuid, status } = props

    return apiClient.postResource(backend, `/projects/predictions/${prediction_uuid}/reviews/`, {
      status: status,
    })
  },

  fetchPredictionReviewHistory: async (props: {
    prediction_uuid: string
    page: number
    pageSize: number
  }): Promise<AxiosResponse<PaginatedPredictionReviewList>> => {
    const { prediction_uuid, page, pageSize } = props

    return apiClient.fetchResource(backend, `/projects/predictions/${prediction_uuid}/reviews/`, {
      page: page,
      page_size: pageSize,
    })
  },
  // Insights
  fetchInsightsReports: async (props: {
    page: number
    page_size: number
    project_uuid: string
    type?: string
    simulation_job?: string
  }): Promise<AxiosResponse<PaginatedReportListCreateList>> => {
    const { project_uuid, page, page_size, simulation_job, type } = props
    return apiClient.fetchResource(backend, `/projects/${project_uuid}/reports/`, {
      page,
      page_size,
      type,
      simulation_job,
    })
  },

  fetchInsightsReport: async (props: { project_uuid: string; report_uuid: string }): Promise<AxiosResponse<Report>> => {
    const { project_uuid, report_uuid } = props

    return apiClient.fetchResource(backend, `/projects/${project_uuid}/reports/${report_uuid}/`)
  },

  fetchFeaturesCountInsight: async (props: {
    project_uuid: string
    report_uuid: string
    insight_uuid: string
    labels?: Array<string> | string
    features?: Array<string> | string
    features_category?: "numerical" | "categorical"
  }): Promise<AxiosResponse<FeaturesCountsInsight>> => {
    const { project_uuid, report_uuid, insight_uuid, labels, features, features_category } = props

    return apiClient.fetchResource(
      backend,
      `/projects/${project_uuid}/reports/${report_uuid}/insights/${insight_uuid}/features-counts-insight/`,
      {
        features_category,
        labels,
        features,
      },
    )
  },

  fetchFeaturesComparisonInsight: async (props: {
    project_uuid: string
    report_uuid: string
    insight_uuid: string
    labels?: Array<string> | string
    reasons?: Array<string> | string
    categorical_feature?: string
    numerical_feature?: string
  }): Promise<AxiosResponse> => {
    const { project_uuid, report_uuid, insight_uuid, labels, categorical_feature, numerical_feature, reasons } = props

    return apiClient.fetchResource(
      backend,
      `/projects/${project_uuid}/reports/${report_uuid}/insights/${insight_uuid}/features-comparison-insight/`,
      {
        categorical_feature,
        numerical_feature,
        labels,
        reasons,
      },
    )
  },

  fetchInsight: async <T>(props: {
    project_uuid: string
    report_uuid: string
    insight_uuid: string
  }): Promise<AxiosResponse<T>> => {
    const { project_uuid, report_uuid, insight_uuid } = props

    return apiClient.fetchResource(
      backend,
      `/projects/${project_uuid}/reports/${report_uuid}/insights/${insight_uuid}/`,
    )
  },

  createInsightsReport: async (
    props: ReportListCreateRequest & { project_uuid: string },
  ): Promise<AxiosResponse<Report>> => {
    const { project_uuid } = props

    return apiClient.postResource(backend, `/projects/${project_uuid}/reports/`, { ...props })
  },

  deleteInsightsReport: async (props: { project_uuid: string; report_uuid: string }): Promise<AxiosResponse> => {
    const { project_uuid, report_uuid } = props

    return apiClient.deleteResource(backend, `/projects/${project_uuid}/reports/${report_uuid}/`)
  },

  cancelInsightsReport: async (props: { project_uuid: string; report_uuid: string }): Promise<AxiosResponse> => {
    const { project_uuid, report_uuid } = props

    return apiClient.postResource(backend, `/projects/${project_uuid}/reports/${report_uuid}/cancel/`)
  },
  // Fetch Actual Decisions List
  fetchActualDecisions: async (props: {
    page: number
    page_size: number
    project_uuid: string
    prediction_uuid: string
  }): Promise<AxiosResponse<PaginatedActualDecisionList>> => {
    const { project_uuid, prediction_uuid, page, page_size } = props

    return apiClient.fetchResource(
      backend,
      `/projects/${project_uuid}/predictions/${prediction_uuid}/actual-decisions/`,
      {
        page,
        page_size,
      },
    )
  },
  // Create Actual Decisions for several predictions
  createActualDecisions: async (
    props: ActualDecisionCreateRequest,
  ): Promise<AxiosResponse<ActualDecisionCreateResponse>> => {
    const { project_uuid, decisions } = props

    return apiClient.postResource(backend, `/projects/${project_uuid}/predictions/actual-decisions/`, [...decisions])
  },
  // List Actual Decisions file creation jobs
  fetchActualDecisionsFileCreationJobs: async (props: {
    project_uuid: string
    page: number
    page_size: number
  }): Promise<AxiosResponse<PaginatedActualDecisionCreationJobList>> => {
    const { project_uuid, page, page_size } = props

    return apiClient.fetchResource(
      backend,
      `/projects/${project_uuid}/predictions/actual-decisions/file-creation-jobs/`,
      {
        page,
        page_size,
      },
    )
  },
  // Create Actual Decisions for several predictions with data file
  createActualDecisionsWithDataFile: async (props: {
    project_uuid: string
    dataFile_uuid: string
  }): Promise<AxiosResponse<ActualDecisionCreationJob>> => {
    const { project_uuid, dataFile_uuid } = props

    return apiClient.postResource(
      backend,
      `/projects/${project_uuid}/predictions/actual-decisions/file-creation-jobs/`,
      {
        data_file: dataFile_uuid,
      },
    )
  },
  // Autofill Actual Decisions
  autofillActualDecisions: async (props: {
    project_uuid: string
    predictions_start_time: string
    predictions_end_time: string
    label_uuid: string
    label_name: string
    use_konan_decision: boolean
    status: "success" | "failed"
  }): Promise<AxiosResponse<ActualDecisionFillResponse>> => {
    const {
      project_uuid,
      predictions_start_time,
      predictions_end_time,
      label_uuid,
      label_name,
      use_konan_decision,
      status,
    } = props

    return apiClient.postResource(backend, `/projects/${project_uuid}/predictions/actual-decisions/fill/`, {
      predictions_start_time,
      predictions_end_time,
      label: label_uuid,
      label_name,
      use_konan_decision,
      status,
    })
  },
  // Delete Actual Decision
  cancelActualDecisionsFile: async (props: {
    project_uuid: string
    file_creation_uuid: string
  }): Promise<AxiosResponse> => {
    const { project_uuid, file_creation_uuid } = props

    return apiClient.deleteResource(
      backend,
      `/projects/${project_uuid}/predictions/actual-decisions/file-creation-jobs/${file_creation_uuid}/cancel`,
    )
  },
  // create component test
  testComponent: async (props: {
    projectId: string
    componentId: string
    componentType: "rulesets" | "scorecards" | "scorecardsets" | "programs" | "taglists" | "calculators"
    body: Record<PropertyKey, unknown>
  }): Promise<AxiosResponse<ComponentTest>> => {
    const { projectId, componentId, body, componentType } = props
    return apiClient.postResource(backend, `/projects/${projectId}/${componentType}/${componentId}/test/`, {
      ...body,
    })
  },
  // get component schema
  getComponentSchema: async (props: {
    projectId: string
    componentId: string
    componentType: "rulesets" | "scorecards" | "scorecardsets" | "programs" | "taglists" | "calculators"
  }): Promise<AxiosResponse<DeploymentSchemaFeatures[]>> => {
    const { projectId, componentId, componentType } = props
    return apiClient.fetchResource(backend, `/projects/${projectId}/${componentType}/${componentId}/schema/`)
  },
  // get fuzzy match results
  getFuzzyMatchResults: async (props: FuzzyMatchRequest): Promise<AxiosResponse<PaginatedFuzzyMatchList>> => {
    const { projectId, params } = props
    return apiClient.fetchResource(backend, `/projects/${projectId}/fuzzy-match-records/`, params)
  },
}
